import { PropsWithChildren, useState, useEffect } from 'react';

import { Box, BoxColors } from '../Box';
import { Icon, IconSize } from '../Icon';
import { Tooltip } from '../Tooltip';
import { Typography } from '../Typography';

import { useStyles } from './GroupAvatar.styles';

type GroupAvatarSize = 'small' | 'medium' | 'large';

type GroupAvatarProps = {
  loadImage: () => Promise<string | undefined>;
  size?: GroupAvatarSize;
  withRadius?: boolean;
  borderSize?: number;
  borderColor?: BoxColors;
};

export const GroupAvatar = ({
  children,
  loadImage,
  size = 'medium',
  borderSize,
  borderColor,
}: PropsWithChildren<GroupAvatarProps>) => {
  const { classes } = useStyles();

  const [src, setSrc] = useState<string>();
  const [hasError, setHasError] = useState(false);

  const handleAvatarIconSize = (variant: GroupAvatarSize): IconSize => {
    switch (variant) {
      case 'large':
        return '48';

      case 'medium':
        return '28';

      case 'small':
        return '20';

      default:
        return '16';
    }
  };

  const handleSetError = () => setHasError(true);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        setSrc(await loadImage());
      } catch (error) {
        setHasError(true);
      }
    };

    fetchImage();
  }, [loadImage]);

  if (hasError) {
    return (
      <Box
        className={classes[size]}
        radius="round"
        position="relative"
        bgcolor="bg.greyscale.primary"
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexShrink={0}
        border={borderSize}
        borderColor={borderColor}
      >
        {children}
        <Tooltip
          placement="top"
          title={
            <Box display="flex" flexDirection="column" gap="8">
              <Typography variant="caption" color="bg.light.primary">
                Не удалось загрузить изображение
              </Typography>
              <Typography variant="caption" color="text.dark.secondary">
                Попробуйте обновить страницу
              </Typography>
            </Box>
          }
        >
          <Icon
            size={handleAvatarIconSize(size)}
            color="stroke.secondary"
            name="picture_slash_outline"
          />
        </Tooltip>
      </Box>
    );
  }

  if (!src) {
    return (
      <Box
        className={classes[size]}
        radius="round"
        position="relative"
        bgcolor="bg.greyscale.secondary"
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexShrink={0}
        border={borderSize}
        borderColor={borderColor}
      >
        {children}
        <Icon
          size={handleAvatarIconSize(size)}
          color="stroke.secondary"
          name="users_3_outline"
        />
      </Box>
    );
  }

  return (
    <Box
      className={classes[size]}
      radius="round"
      position="relative"
      bgcolor="bg.greyscale.primary"
      flexShrink={0}
    >
      {children}
      <img
        src={src}
        alt="avatar"
        className={classes.image}
        onError={handleSetError}
      />
    </Box>
  );
};
